/**
 * Matriz de Fechamento Estratégico — tabela curada de referência.
 *
 * Para cada loteria define:
 *   - K fixo (dezenas por jogo = aposta mínima da Caixa)
 *   - V permitidos (quantas dezenas o usuário escolhe)
 *   - T permitidos (meta de acertos garantidos)
 *   - targets[v][t] = número mínimo conhecido de jogos (referência publicada)
 *
 * Os valores de `targets` são os ótimos atualmente publicados no
 * Covering Repository (coveringrepository.com) — a referência mundial
 * para covering designs e wheels. Para Mega-Sena/Quina/Dupla-Sena usamos
 * C(v, k, t); para Lotofácil usamos L(v, 15, 15, t). Quando o gerador
 * interno reproduz exatamente esse número, a UI marca como "Exato".
 */

import type { LotterySlug } from "./pool-math";

export interface StrategicConfig {
  /** Dezenas por jogo (= aposta mínima da Caixa). */
  k: number;
  /** Quantas dezenas o usuário pode escolher. */
  vOptions: number[];
  /** Meta de acertos permitida. */
  tOptions: number[];
  /** Mapa target[v][t] = jogos. */
  targets: Record<number, Record<number, number>>;
}

export const STRATEGIC: Record<LotterySlug, StrategicConfig> = {
  "mega-sena": {
    k: 6,
    vOptions: [10, 12, 14, 16, 18],
    tOptions: [4, 5],
    targets: {
      10: { 4: 20, 5: 50 },
      12: { 4: 41, 5: 132 },
      14: { 4: 80, 5: 371 },
      16: { 4: 152, 5: 808 },
      18: { 4: 236, 5: 1530 },
    },
  },
  "dupla-sena": {
    k: 6,
    vOptions: [10, 12, 14, 16, 18],
    tOptions: [4, 5],
    targets: {
      10: { 4: 20, 5: 50 },
      12: { 4: 41, 5: 132 },
      14: { 4: 80, 5: 371 },
      16: { 4: 152, 5: 808 },
      18: { 4: 236, 5: 1530 },
    },
  },
  quina: {
    k: 5,
    vOptions: [14, 16, 18, 20, 22, 24],
    tOptions: [3, 4],
    targets: {
      14: { 3: 43, 4: 229 },
      16: { 3: 65, 4: 404 },
      18: { 3: 94, 4: 664 },
      20: { 3: 133, 4: 1063 },
      22: { 3: 172, 4: 1573 },
      24: { 3: 231, 4: 2237 },
    },
  },
  lotofacil: {
    k: 15,
    vOptions: [16, 18, 20, 22, 24],
    tOptions: [13, 14],
    targets: {
      16: { 13: 1, 14: 1 },
      18: { 13: 6, 14: 24 },
      20: { 13: 32, 14: 350 },
      22: { 13: 246, 14: 2610 },
      24: { 13: 1249, 14: 19805 },
    },
  },
};

export function getStrategicTarget(
  slug: LotterySlug,
  v: number,
  t: number,
): number | null {
  const cfg = STRATEGIC[slug];
  if (!cfg) return null;
  return cfg.targets[v]?.[t] ?? null;
}

/** Lista de v aceitos formatada para mensagens. */
export function strategicVHint(slug: LotterySlug): string {
  const opts = STRATEGIC[slug].vOptions;
  return opts.slice(0, -1).join(", ") + " ou " + opts[opts.length - 1];
}

/** Lista de t aceitos formatada para mensagens. */
export function strategicTHint(slug: LotterySlug): string {
  const opts = STRATEGIC[slug].tOptions;
  return opts.join(" ou ");
}